import { ILicensePlateLookupResult, ILicensePlateLookupResultValue } from "../BusinessObjects/LicensePlateLookupResponse";
import { ILicensePlateLookupValueMap, LicensePlateLookupValueFieldType, ILookupCodeTranslation } from "../LicensePlateLookupOptions";
import Api from '../../../../../services/Api';
import { ApiResponse } from 'apisauce';
import {toast} from "react-toastify";
import { ComponentUtils } from '../../../AutoComplete/lib/business-objects/ComponentUtils';


export namespace LicensePlateLookupUtils {
  export const LicensePlateSearch = async (value: any, url: string): Promise<any> => {
    const plateNumber = (value?.PlateNumber || '').trim();
    const noResultMessage = plateNumber
      ? `The API returned no result for license plate number ${plateNumber}.`
      : 'The API returned no result for that license plate number.';

    return Api.post(url, value)
      .then((response: ApiResponse<any>) => {
        if (response.status === 204) {
          toast.warn(noResultMessage);
          return null;
        }

        if (!response.ok) {
          const errorMessage = response.data?.message || response.data?.error || '';
          const statusText = response.status ? ` (${response.status})` : '';

          if (errorMessage.toLowerCase().includes('returned no records for plate')) {
            toast.warn(noResultMessage);
            return null;
          }

          const message = errorMessage
            ? `License plate validation failed${statusText}: ${errorMessage}`
            : `License plate validation service call failed${statusText}.`;
          toast.warn(message);
          return null;
        }

        const data = response.data;
        if (!data) {
          toast.warn(noResultMessage);
          return null;
        }

        return data;
      })
      .catch((error) => {
        console.log('license plate lookup error', error);
        toast.warn('License plate validation request failed before reaching the API.');
        return null;
      });
  };

  export const ProcessLicensePlate = async (data: ILicensePlateLookupResult, component: any, form: any) => {
    if (
        !form ||
        !data
    ) {
      return;
    }

    var components: ILicensePlateLookupValueMap[] = component.compMaps || [];

    for (let i = 0; i < components.length; i++) {
      const valueMap: ILicensePlateLookupValueMap = components[i];
      const lookupResultValue: ILicensePlateLookupResultValue = getNestedObject(data,valueMap.lookupFieldName.split("."));
      console.log("vehicle data:", lookupResultValue);
      if (!lookupResultValue) continue;

      mapValueToComponent(lookupResultValue, valueMap, form, component);
    }

    mapCommonVehicleFields(data, form, component);
  };

  export const ProcessVinLookup = async (data: ILicensePlateLookupResult, component: any, form: any) => {
    if (!form || !data) {
      return;
    }

    mapCommonVehicleFields(data, form, component);
  };

  const mapValueToComponent = (
      lookupResultValue: any,
      valueMap: ILicensePlateLookupValueMap,
      form: any,
      licensePlateComponent: any
  ) => {
    var changeComponent = findComponentByPath(form, valueMap.modelFieldName);

    if (!changeComponent) return;

    var componentValue: any =
        valueMap.lookupFieldType == LicensePlateLookupValueFieldType.Code
          ? (lookupResultValue && lookupResultValue.valueId !== undefined ? lookupResultValue.valueId : lookupResultValue)
          : lookupResultValue;

    if (componentValue === undefined || componentValue === null) return;

    const isPlateTypeMapping = /plate\s*type/i.test(valueMap.lookupFieldName || valueMap.modelFieldName || '');

    if (!isPlateTypeMapping && componentValue && valueMap.codeTranslations && valueMap.codeTranslations.length > 0) {
      let translatedValue = "";

      for (const translation of valueMap.codeTranslations) {
        if (translation.lookupValue == componentValue) {
          translatedValue = translation.outputValue ?? "";
          break;
        }
      }

      componentValue = translatedValue;
    }

    if (shouldAssignComponentValue(changeComponent, licensePlateComponent.overrideExistingValues)) {
      changeComponent.setValue(componentValue);
    }
  };

  function mapCommonVehicleFields(data: any, form: any, licensePlateComponent: any) {
    const vin =
      getValueFromPaths(data, [
        'Vehicle.VehicleIdentificationNumber',
        'vehicle.vehicleIdentificationNumber',
        'VehicleIdentificationNumber',
        'vehicleIdentificationNumber',
        'Vehicle.Vin',
        'vehicle.vin',
        'Vin',
        'vin',
        'Vehicle.VIN',
        'vehicle.VIN',
        'VIN',
        'Vehicle.VINNumber',
        'vehicle.VINNumber',
        'VINNumber',
        'vinNumber',
        'Vehicle.VinNumber',
        'vehicle.vinNumber',
        'Record.Vehicle.VehicleIdentificationNumber',
        'record.vehicle.vehicleIdentificationNumber',
        'Record.Vehicle.VIN',
        'record.vehicle.vin',
        'Record.Vehicle.VINNumber',
        'record.vehicle.vinNumber',
        'Record.VIN',
        'record.vin',
        'Record.0.Vehicle.VehicleIdentificationNumber',
        'record.0.vehicle.vehicleIdentificationNumber',
        'Record.0.Vehicle.VIN',
        'record.0.vehicle.vin',
        'Record.0.Vehicle.VINNumber',
        'record.0.vehicle.vinNumber',
        'Record.0.VIN',
        'record.0.vin',
        'Records.0.Vehicle.VehicleIdentificationNumber',
        'records.0.vehicle.vehicleIdentificationNumber',
        'Records.0.Vehicle.VIN',
        'records.0.vehicle.vin',
        'Records.0.Vehicle.VINNumber',
        'records.0.vehicle.vinNumber',
        'Vehicle.VehicleIdentificationNo',
        'vehicle.vehicleIdentificationNo',
        'VehicleIdentificationNo',
        'vehicleIdentificationNo',
        'VehicleData.VehicleIdentificationNumber',
        'vehicleData.vehicleIdentificationNumber',
        'VehicleData.VIN',
        'vehicleData.vin',
        'VehicleData.VINNumber',
        'vehicleData.vinNumber'
      ]) || '';

    const licensePlateNumber =
      getValueFromPaths(data, [
        'VehiclePlate.FormattedPlateId',
        'vehiclePlate.formattedPlateId',
        'VehiclePlate.FormattedPlateNumber',
        'vehiclePlate.formattedPlateNumber',
        'VehiclePlate.PlateNumber',
        'vehiclePlate.plateNumber',
        'VehiclePlate.PlateId',
        'vehiclePlate.plateId',
        'VehiclePlate.Plate',
        'vehiclePlate.plate',
        'Vehicle.VehiclePlate.PlateNumber',
        'vehicle.vehiclePlate.plateNumber',
        'Vehicle.VehiclePlate.FormattedPlateId',
        'vehicle.vehiclePlate.formattedPlateId',
        'Vehicle.VehiclePlate.FormattedPlateNumber',
        'vehicle.vehiclePlate.formattedPlateNumber',
        'Vehicle.VehiclePlate.PlateId',
        'vehicle.vehiclePlate.plateId',
        'Vehicle.PlateNumber',
        'vehicle.plateNumber',
        'Vehicle.PlateId',
        'vehicle.plateId',
        'Vehicle.FormattedPlateId',
        'vehicle.formattedPlateId',
        'Vehicle.FormattedPlateNumber',
        'vehicle.formattedPlateNumber',
        'Record.Vehicle.VehiclePlate.PlateNumber',
        'record.vehicle.vehiclePlate.plateNumber',
        'Record.Vehicle.VehiclePlate.FormattedPlateId',
        'record.vehicle.vehiclePlate.formattedPlateId',
        'Record.Vehicle.VehiclePlate.FormattedPlateNumber',
        'record.vehicle.vehiclePlate.formattedPlateNumber',
        'Record.Vehicle.PlateNumber',
        'record.vehicle.plateNumber',
        'Record.0.Vehicle.VehiclePlate.PlateNumber',
        'record.0.vehicle.vehiclePlate.plateNumber',
        'Record.0.Vehicle.VehiclePlate.FormattedPlateId',
        'record.0.vehicle.vehiclePlate.formattedPlateId',
        'Record.0.Vehicle.PlateNumber',
        'record.0.vehicle.plateNumber',
        'Records.0.Vehicle.VehiclePlate.PlateNumber',
        'records.0.vehicle.vehiclePlate.plateNumber',
        'Records.0.Vehicle.PlateNumber',
        'records.0.vehicle.plateNumber',
        'VehiclePlateNumber',
        'PlateNumber',
        'PlateNo',
        'LicensePlateNo',
        'LicensePlateNumber',
        'plateNumber',
        'plateNo',
        'licensePlateNo',
        'licensePlateNumber',
        'formattedPlateId',
        'formattedPlateNumber',
        'vehiclePlateNumber',
        'vehicle.vehiclePlate.formattedPlateId'
      ]) || '';

    const plateState =
      getValueFromPaths(data, [
        'Vehicle.VehiclePlateState',
        'vehicle.vehiclePlateState',
        'VehiclePlateState',
        'vehiclePlateState',
        'VehicleRegistration.State',
        'vehicleRegistration.state'
      ]) || '';

    const registrationYear =
      getValueFromPaths(data, [
        'Vehicle.VehicleRegistrationYear',
        'vehicle.vehicleRegistrationYear',
        'VehicleRegistrationYear',
        'vehicleRegistrationYear'
      ]) || '';

    const vehicleMake =
      getValueFromPaths(data, [
        'Vehicle.VehicleMake',
        'vehicle.vehicleMake',
        'VehicleMake',
        'vehicleMake',
        'Vehicle.MakeDescription',
        'vehicle.makeDescription',
        'Vehicle.Make',
        'vehicle.make',
        'Make',
        'make',
        'vehicle.makeDescription'
      ]) || '';

    const vehicleModel =
      getValueFromPaths(data, [
        'Vehicle.VehicleModel',
        'vehicle.vehicleModel',
        'VehicleModel',
        'vehicleModel',
        'Vehicle.Model',
        'vehicle.model',
        'Model',
        'model'
      ]) || '';

    const vehicleModelYear =
      getValueFromPaths(data, [
        'Vehicle.VehicleModelYear',
        'vehicle.vehicleModelYear',
        'VehicleModelYear',
        'vehicleModelYear',
        'Vehicle.Year',
        'vehicle.year',
        'ModelYear',
        'modelYear',
        'vehicleYear',
        'year'
      ]) || '';

    const vehicleColor =
      getValueFromPaths(data, [
        'Vehicle.VehicleColor',
        'vehicle.vehicleColor',
        'VehicleColor',
        'vehicleColor',
        'Vehicle.PrimaryColor',
        'vehicle.primaryColor',
        'PrimaryColor',
        'primaryColor',
        'Color',
        'color'
      ]) || '';

    const licensePlateType =
      getValueFromPaths(data, [
        'Vehicle.LicensePlateType',
        'vehicle.licensePlateType',
        'LicensePlateType',
        'licensePlateType',
        'Vehicle.PlateType',
        'vehicle.plateType',
        'PlateType',
        'plateType',
        'Vehicle.VehiclePlate.PlateType',
        'vehicle.vehiclePlate.plateType'
      ]) || '';

    const normalizedLicensePlateNumber = normalizePlateValue(licensePlateNumber);

    setRawMatchingComponentValues(
      form,
      [
        'licensePlateLookup',
        'licensePlateNumber',
        'licensePlateNumber1',
        'plateNumber',
        'vehiclePlateNumber',
        'formattedPlateId'
      ],
      ['license plate no', 'license plate number', 'plate number'],
      normalizedLicensePlateNumber,
      licensePlateComponent,
      true
    );
    setMatchingComponentTypeValues(
      form,
      ['licensePlateLookup'],
      normalizedLicensePlateNumber,
      licensePlateComponent,
      true
    );

    setRawMatchingComponentValues(
      form,
      [
        'VIN',
        'vin',
        'vehicleIdentificationNumber',
        'vehicleIdentificationNumber1',
        'vehicleVin',
        'vehicleVIN',
        'vinNumber'
      ],
      ['vehicle identification number'],
      vin,
      licensePlateComponent,
      true
    );

    syncFormDataAliases(form, ['vin', 'vehicleIdentificationNumber', 'vehicleVin', 'vehicleVIN', 'VIN'], vin);
    syncFormDataAliases(
      form,
      ['licensePlateNumber', 'LicensePlateNumber', 'plateNumber', 'PlateNumber', 'formattedPlateId', 'FormattedPlateId'],
      normalizedLicensePlateNumber
    );
    setRawMatchingComponentValues(form, ['PlateState', 'vehiclePlateState', 'state', 'licenseState', 'licensePlateState'], ['plate state', 'registration state', 'state', 'license state'], plateState, licensePlateComponent);
    setRawMatchingComponentValues(form, ['RegistrationYear', 'vehicleRegistrationYear', 'registrationYear', 'regYear', 'registrationYear1', 'licensePlateRegistrationYear', 'licensePlateRegistrationYear1', 'vehicleRegistrationYear1'], ['registration year', 'reg year', 'license plate registration year'], registrationYear, licensePlateComponent);
    setFirstMatchingComponentValue(form, ['Make', 'vehicleMake', 'vehicleMake1', 'make', 'vehicleMakeDesc', 'vehicleMakeDescription'], ['vehicle make', 'make'], vehicleMake, licensePlateComponent);
    setFirstMatchingComponentValue(form, ['Model', 'vehicleModel', 'vehicleModel1', 'model', 'model1'], ['vehicle model', 'model'], vehicleModel, licensePlateComponent);
    setFirstMatchingComponentValue(form, ['ModelYear', 'vehicleModelYear', 'vehicleYear', 'year', 'modelYear', 'modelYear1', 'vehicleYear1'], ['vehicle model year', 'model year', 'vehicle year', 'year'], vehicleModelYear, licensePlateComponent);
    setFirstMatchingComponentValue(form, ['Color', 'vehicleColor', 'vehicleColor1', 'vehicleColor2', 'color'], ['vehicle color', 'color'], vehicleColor, licensePlateComponent);
    setRawMatchingComponentValues(form, ['LicensePlateType', 'licensePlateType1', 'plateType', 'vehiclePlateType'], ['license plate type', 'plate type'], licensePlateType, licensePlateComponent, true);
  }

  const setRawMatchingComponentValues = (
    form: any,
    componentKeys: string[],
    labelHints: string[],
    value: any,
    licensePlateComponent: any,
    forceOverride: boolean = false
  ) => {
    if (value === undefined || value === null || value === '') {
      return;
    }

    const matchingComponents = findMatchingComponents(form, componentKeys, labelHints, licensePlateComponent);

    for (const changeComponent of matchingComponents) {
      if (!isCompatibleValueForComponent(changeComponent, value)) {
        continue;
      }

      if (forceOverride || shouldAssignComponentValue(changeComponent, licensePlateComponent.overrideExistingValues)) {
        changeComponent.setValue(value);
      }
    }
  };

  const setFirstMatchingComponentValue = (
    form: any,
    componentKeys: string[],
    labelHints: string[],
    value: any,
    licensePlateComponent: any
  ) => {
    const matchingComponents = findMatchingComponents(form, componentKeys, labelHints, licensePlateComponent);
    const changeComponent = matchingComponents[0];

    if (!changeComponent || value === undefined || value === null || value === '') {
      return;
    }

    if (shouldAssignComponentValue(changeComponent, licensePlateComponent.overrideExistingValues)) {
      if (!isCompatibleValueForComponent(changeComponent, value)) {
        return;
      }

      changeComponent.setValue(value);
    }
  };

  const setMatchingComponentTypeValues = (
    form: any,
    componentTypes: string[],
    value: any,
    sourceComponent: any,
    forceOverride: boolean = false
  ) => {
    if (value === undefined || value === null || value === '') {
      return;
    }

    const matchingComponents = findComponentsByType(form, componentTypes, sourceComponent);
    for (const changeComponent of matchingComponents) {
      if (!isCompatibleValueForComponent(changeComponent, value)) {
        continue;
      }

      if (forceOverride || shouldAssignComponentValue(changeComponent, sourceComponent?.overrideExistingValues)) {
        changeComponent.setValue(value);
      }
    }
  };

  const syncFormDataAliases = (form: any, keys: string[], value: any) => {
    if (value === undefined || value === null || value === '') {
      return;
    }

    const rootData = form?.data ?? form?._data ?? form?.root?.data ?? {};
    for (const key of keys) {
      if (!key) continue;
      assignValueToObject(rootData, key, value);
      assignValueToObject(form?.data, key, value);
      assignValueToObject(form?._data, key, value);
      assignValueToObject(form?.root?.data, key, value);
      assignNestedUnitAliasValues(rootData, key, value);
      assignNestedUnitAliasValues(form?.data, key, value);
      assignNestedUnitAliasValues(form?._data, key, value);
      assignNestedUnitAliasValues(form?.root?.data, key, value);
    }
  };

  const assignValueToObject = (target: any, key: string, value: any) => {
    if (!target || typeof target !== 'object') {
      return;
    }

    target[key] = value;

    if (Array.isArray(target.Units)) {
      for (const unit of target.Units) {
        if (unit && typeof unit === 'object') {
          unit[key] = value;
        }
      }
    }
  };

  const assignNestedUnitAliasValues = (target: any, key: string, value: any) => {
    if (!target || typeof target !== 'object') {
      return;
    }

    const candidateUnitCollections = [
      target.Units,
      target.units,
      target?.data?.Units,
      target?.data?.units,
      target?.submission?.data?.Units,
      target?.submission?.data?.units
    ];

    const altKey = key === 'VIN' ? 'vin' : key === 'vin' ? 'VIN' : key;

    for (const units of candidateUnitCollections) {
      if (!Array.isArray(units)) {
        continue;
      }

      for (const unit of units) {
        if (!unit || typeof unit !== 'object') {
          continue;
        }

        unit[key] = value;
        if (altKey !== key) {
          unit[altKey] = value;
        }
      }
    }
  };

  const setAllMatchingComponentValues = (
    form: any,
    componentKeys: string[],
    labelHints: string[],
    value: any,
    licensePlateComponent: any
  ) => {
    if (value === undefined || value === null || value === '') {
      return;
    }

    const matchingComponents = findMatchingComponents(form, componentKeys, labelHints, licensePlateComponent);
    for (const changeComponent of matchingComponents) {
      if (shouldAssignComponentValue(changeComponent, licensePlateComponent.overrideExistingValues)) {
        changeComponent.setValue(value);
      }
    }
  };

  const shouldAssignComponentValue = (changeComponent: any, overrideExistingValues: boolean) => {
    if (overrideExistingValues) {
      return true;
    }

    const currentValue = typeof changeComponent?.getValue === 'function'
      ? changeComponent.getValue()
      : undefined;

    if (currentValue === undefined || currentValue === null) {
      return true;
    }

    if (typeof currentValue === 'string') {
      const trimmedValue = currentValue.trim();
      if (!trimmedValue) {
        return true;
      }

      if (!/[a-z0-9]/i.test(trimmedValue)) {
        return true;
      }
    }

    return false;
  };

  const findMatchingComponents = (
    form: any,
    componentKeys: string[],
    labelHints: string[],
    sourceComponent?: any
  ) => {
    const matches: any[] = [];
    const seen = new Set<any>();

    for (const componentKey of componentKeys || []) {
      const component = findComponentByPath(form, componentKey, sourceComponent);
      if (component && !seen.has(component)) {
        seen.add(component);
        matches.push(component);
      }
    }

    for (const component of findComponentsByKey(form, componentKeys, sourceComponent)) {
      if (!seen.has(component)) {
        seen.add(component);
        matches.push(component);
      }
    }

    for (const component of findComponentsByLabel(form, labelHints, sourceComponent)) {
      if (!seen.has(component)) {
        seen.add(component);
        matches.push(component);
      }
    }

    return matches;
  };

  const findComponentByPath = (form: any, componentPath: string, sourceComponent?: any) => {
    const component = ComponentUtils.getComponent(form, componentPath);
    if (!component) {
      return null;
    }

    return isAssignableTargetComponent(component, sourceComponent)
      ? component
      : null;
  };

  const findComponentsByKey = (form: any, componentKeys: string[], sourceComponent?: any) => {
    const traversalRoots = getComponentTraversalRoots(form);
    if (!traversalRoots.length || !componentKeys || !componentKeys.length) {
      return [];
    }

    const normalizedKeys = componentKeys.map(normalizeText);
    const foundComponents: any[] = [];
    const seen = new Set<any>();

    for (const traversalRoot of traversalRoots) {
      traversalRoot.everyComponent((component: any) => {
        const componentKey = normalizeText(component?.key || component?.component?.key || '');
        if (
          componentKey &&
          normalizedKeys.includes(componentKey) &&
          isAssignableTargetComponent(component, sourceComponent) &&
          !seen.has(component)
        ) {
          seen.add(component);
          foundComponents.push(component);
        }

        return true;
      });
    }

    return foundComponents;
  };

  const findComponentsByLabel = (form: any, labelHints: string[], sourceComponent?: any) => {
    const traversalRoots = getComponentTraversalRoots(form);
    if (!traversalRoots.length || !labelHints || !labelHints.length) {
      return [];
    }

    const normalizedHints = labelHints.map(normalizeText);
    const foundComponents: any[] = [];
    const seen = new Set<any>();

    for (const traversalRoot of traversalRoots) {
      traversalRoot.everyComponent((component: any) => {
        const componentLabel = normalizeText(component?.label || component?.component?.label || '');
        if (
          componentLabel &&
          normalizedHints.some((hint) => componentLabel.includes(hint)) &&
          isAssignableTargetComponent(component, sourceComponent) &&
          !seen.has(component)
        ) {
          seen.add(component);
          foundComponents.push(component);
        }

        return true;
      });
    }

    return foundComponents;
  };

  const findComponentsByType = (form: any, componentTypes: string[], sourceComponent?: any) => {
    const traversalRoots = getComponentTraversalRoots(form);
    if (!traversalRoots.length || !componentTypes || !componentTypes.length) {
      return [];
    }

    const normalizedTypes = componentTypes.map(normalizeText);
    const foundComponents: any[] = [];
    const seen = new Set<any>();

    for (const traversalRoot of traversalRoots) {
      traversalRoot.everyComponent((component: any) => {
        const componentType = normalizeText(component?.component?.type || component?.type || '');
        if (
          componentType &&
          normalizedTypes.includes(componentType) &&
          isAssignableTargetComponent(component, sourceComponent) &&
          !seen.has(component)
        ) {
          seen.add(component);
          foundComponents.push(component);
        }

        return true;
      });
    }

    return foundComponents;
  };

  const isAssignableTargetComponent = (component: any, sourceComponent?: any) => {
    if (
      typeof component?.getValue !== 'function' ||
      typeof component?.setValue !== 'function'
    ) {
      return false;
    }

    const componentType = component?.component?.type || component?.type;
    const componentKey = component?.component?.key || component?.key;
    const sourceType = sourceComponent?.component?.type || sourceComponent?.type;
    const sourceKey = sourceComponent?.component?.key || sourceComponent?.key;
    const isSameInstance = sourceComponent && component === sourceComponent;
    const isSameKey = sourceKey && componentKey && sourceKey === componentKey;
    const isSameType = sourceType && componentType && sourceType === componentType;

    if (isSameInstance || (isSameKey && isSameType)) {
      return false;
    }

    return true;
  };

  const isCompatibleValueForComponent = (component: any, value: any) => {
    const componentType = (component?.component?.type || component?.type || '').toLowerCase();

    if (componentType === 'checkbox') {
      return typeof value === 'boolean' || value === 0 || value === 1;
    }

    return true;
  };

  const getComponentTraversalRoots = (form: any) => {
    const traversalRoots: any[] = [];
    const seen = new Set<any>();

    const addRoot = (candidate: any) => {
      if (candidate && typeof candidate.everyComponent === 'function' && !seen.has(candidate)) {
        seen.add(candidate);
        traversalRoots.push(candidate);
      }
    };

    addRoot(form);

    let parent = form?.parent;
    while (parent) {
      addRoot(parent);
      parent = parent.parent;
    }

    addRoot(form?.root);

    return traversalRoots;
  };

  const getValueFromPaths = (data: any, paths: string[]) => {
    for (const path of paths) {
      const value = getNestedObject(data, path.split('.'));
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
    }

    return undefined;
  };

  const normalizePlateValue = (value: any) => {
    if (value === undefined || value === null) {
      return '';
    }

    const normalized = String(value).trim().replace(/[^a-z0-9]/gi, '');
    return normalized.toUpperCase();
  };

  const normalizeText = (value: string) => (value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

  const getNestedObject = (nestedObj, pathArr) => {
    return pathArr.reduce((obj, key) =>
        (obj && obj[key] !== 'undefined') ? obj[key] : undefined, nestedObj);
  }
}
